import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE_URL, fetchWithCsrf } from '../../../config/api';
import DashboardFooter from '../components/DashboardFooter';
import ScoringConsole from './ScoringConsole';
import DomainExplorer from './DomainExplorer';
import AlertPublisher from './AlertPublisher';
import IntakeInbox from './IntakeInbox';
import UsersDirectory from './UsersDirectory';
import AdminAccess from './AdminAccess';
import { useAdminRole } from '../../../hooks/useIsAdmin';
import { REGISTRY, visibleTabs } from './adminPermissions';

// Five tabs (10-01). The tab lives in ?tab= so a refresh or Back keeps it.
// Since 10-07 a tab only shows when the admin holds at least one of its cards
// (per-card permissions, see adminPermissions.js).
const TABS = REGISTRY.map((t) => ({ id: t.id, label: t.label }));

function TabBar({ tabs, active, onSelect }) {
  return (
    <div className="flex items-center gap-2 border-b border-gray-300 mb-6" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={active === t.id}
          onClick={() => onSelect(t.id)}
          className={`px-4 py-2 -mb-px text-lg whitespace-nowrap border-b-2 transition-colors ${
            active === t.id ? 'text-black font-semibold border-gray-800' : 'text-gray-600 hover:text-black border-transparent'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Section({ title, children, right }) {
  return (
    <div className="border border-gray-200 rounded-xl p-5 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-black">{title}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="border border-gray-200 rounded-xl p-4 bg-white">
      <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
      <p className={`text-2xl font-bold mt-1 leading-tight ${accent || 'text-black'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function Bar({ label, n, max, color }) {
  const pct = max > 0 ? Math.max(2, Math.round((n / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-32 flex-shrink-0 capitalize text-gray-600 truncate" title={label}>{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
        <div className={`h-full rounded-full ${color || 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-16 text-right text-gray-500 tabular-nums">{(n || 0).toLocaleString()}</span>
    </div>
  );
}

// Throwaway flag taxonomy (from the scanner audit). The scanner will ship a
// canonical apps/scanner/scoring/flag_taxonomy.json with the re-tune — switch
// to it then instead of growing this map.
const FLAG_BUCKET = {
  BLOCKLISTED_DOMAIN: 'gold', SPAMHAUS_PHISHING: 'gold', RDAP_PENDING_DELETE: 'gold',
  DOMAIN_NEWBORN: 'gold', DOMAIN_INFANT: 'gold', HOSTING_HIGH_RISK_COUNTRY: 'gold',
  NO_A_RECORDS: 'liveness', NO_HTTPS: 'liveness', HTTP_ERROR_STATUS: 'liveness', NO_TLS_CERTIFICATE: 'liveness',
  NO_DNSSEC: 'noise', LOW_TTL: 'noise', NO_CSP: 'noise', NO_HSTS: 'noise',
  OBSERVATORY_FAILING: 'noise', NO_SITEMAP: 'noise', NO_ROBOTS_TXT: 'noise',
};
const BUCKET_STYLE = {
  gold: 'bg-red-100 text-red-800 border-red-200',
  liveness: 'bg-amber-100 text-amber-800 border-amber-200',
  noise: 'bg-gray-100 text-gray-500 border-gray-200',
  contextual: 'bg-blue-50 text-blue-700 border-blue-200',
};
const BUCKET_LABEL = { gold: 'Risk signals', liveness: 'Reachability', noise: 'Noise (de-weight pending)', contextual: 'Contextual' };

const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => (n == null ? '—' : Number(n).toLocaleString());
const timeAgo = (iso) => {
  if (!iso) return 'never';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const isRecent = (iso, mins = 60) => iso && (Date.now() - new Date(iso).getTime()) < mins * 60 * 1000;
// Progress toward the NEXT seed version: top-25k domains not re-ranked since
// the last head scan. They only enter the rescan queue once they age past the
// scanner's 60-day window, so until then the count sits at ~25k while the
// queue reads 0 - say so, instead of making it look like overdue work.
const RESCAN_WINDOW_DAYS = 60;
const headBumpNote = (sc) => {
  if (!(sc.headLeft > 0)) {
    return sc.scanMeta?.last_full_at ? `full ${timeAgo(sc.scanMeta.last_full_at)}` : 'no full pass yet';
  }
  const lastHead = sc.scanMeta?.last_head_at ? new Date(sc.scanMeta.last_head_at).getTime() : null;
  const dueInDays = lastHead ? Math.ceil((lastHead + RESCAN_WINDOW_DAYS * 864e5 - Date.now()) / 864e5) : 0;
  return dueInDays > 0
    ? `next seed bump: ${num(sc.headLeft)} top-25k due for rescan in ~${dueInDays}d`
    : `next seed bump: ${num(sc.headLeft)} top-25k left to rescan`;
};
const maxOf = (arr, key = 'n') => Math.max(1, ...(arr || []).map((x) => x[key] || 0));
const riskColor = (lvl) => {
  const l = (lvl || '').toLowerCase();
  if (l.includes('legit') || l.includes('safe') || l.includes('low')) return 'bg-green-500';
  if (l.includes('scam') || l.includes('high') || l.includes('malic')) return 'bg-red-500';
  return 'bg-amber-500';
};
const statusColor = (s) => {
  const v = (s || '').toLowerCase();
  if (v === 'active') return 'bg-green-500';
  if (v === 'canceled' || v === 'cancelled') return 'bg-gray-400';
  if (v === 'past_due') return 'bg-red-500';
  return 'bg-blue-500';
};

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [health, setHealth] = useState(null);
  const [sourceHealth, setSourceHealth] = useState(null);
  const [meEmail, setMeEmail] = useState(null);
  const { role: meRole, permissions, can } = useAdminRole();
  const [announce, setAnnounce] = useState({ title: '', body: '' });
  const [announcing, setAnnouncing] = useState(false);
  const [announceMsg, setAnnounceMsg] = useState(null);

  // Domain inspector + re-rank
  const [inspectQuery, setInspectQuery] = useState('');
  const [inspecting, setInspecting] = useState(false);
  const [inspect, setInspect] = useState(null);
  const [inspectErr, setInspectErr] = useState(null);
  const [rerankReason, setRerankReason] = useState('');
  const [rerankMsg, setRerankMsg] = useState(null);
  const [rerankList, setRerankList] = useState([]);

  // Only the data behind cards this admin can open is fetched: the server would
  // 403 the rest anyway, and a 403 should not surface as a broken card.
  const wantsUsers = can('users', 'directory') || can('users', 'access');
  const wantsAnalytics = ['overview', 'revenue', 'dta', 'growth'].some((c) => can('general', c)) || can('scanner', 'stats');
  const wantsHealth = can('general', 'health');
  const wantsSourceHealth = can('scanner', 'sourceHealth');
  const wantsInspector = can('scanner', 'inspector');

  useEffect(() => {
    if (!permissions) return;
    if (wantsUsers) {
      fetch(`${API_BASE_URL}/bff/admin/users?limit=500`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : { users: [] }))
        .then((d) => setUsers(d.users || []))
        .catch(() => {})
        .finally(() => setUsersLoading(false));
    }
    if (wantsAnalytics) {
      fetch(`${API_BASE_URL}/bff/admin/analytics`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null)).then(setAnalytics).catch(() => {});
    }
    if (wantsHealth) {
      fetch(`${API_BASE_URL}/bff/admin/health`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null)).then(setHealth).catch(() => {});
    }
    if (wantsSourceHealth) {
      fetch(`${API_BASE_URL}/bff/admin/source-health`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null)).then(setSourceHealth).catch(() => {});
    }
    if (wantsInspector) {
      fetch(`${API_BASE_URL}/bff/admin/rerank?limit=10`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : { items: [] })).then((d) => setRerankList(d.items || [])).catch(() => {});
    }
    fetch(`${API_BASE_URL}/bff/profile`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setMeEmail(d?.profile?.email || null))
      .catch(() => {});
  }, [permissions, wantsUsers, wantsAnalytics, wantsHealth, wantsSourceHealth, wantsInspector]);

  // Elevated = superadmin: full access everywhere and the only tier that can
  // change another elevated admin. Everything else is per card (can()). The
  // server enforces both; this only decides what to render.
  const isElevated = meRole === 'superadmin';

  // Returns null on success or a message to show. Not optimistic: a role change
  // can legitimately be refused (last elevated admin, self-demotion), and
  // showing the new role anyway would misreport who can do what.
  const setRole = async (u, role, perms) => {
    const previous = u.role;
    setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, role } : x)));
    try {
      const r = await fetchWithCsrf(`${API_BASE_URL}/bff/admin/users/${u.id}/role`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(perms ? { role, permissions: perms } : { role }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, role: previous } : x)));
        return err.message || err.error || `Failed to change role (HTTP ${r.status})`;
      }
      const data = await r.json().catch(() => ({}));
      if (data.user) {
        setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, role: data.user.role, admin_permissions: data.user.admin_permissions } : x)));
      }
      return null;
    } catch {
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, role: previous } : x)));
      return 'Network error — role unchanged.';
    }
  };

  // Per-card permissions for an existing admin (10-07). Same contract as
  // setRole: null on success, else a message. Not optimistic either - the
  // server may refuse a card the caller doesn't hold themselves.
  const setPermissions = async (u, perms) => {
    try {
      const r = await fetchWithCsrf(`${API_BASE_URL}/bff/admin/users/${u.id}/permissions`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissions: perms }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        return err.message || err.error || `Failed to save access (HTTP ${r.status})`;
      }
      const data = await r.json().catch(() => ({}));
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, admin_permissions: data.user?.admin_permissions || perms } : x)));
      return null;
    } catch {
      return 'Network error — access unchanged.';
    }
  };

  const submitAnnounce = async (e) => {
    e.preventDefault();
    if (!announce.title.trim()) return;
    setAnnouncing(true); setAnnounceMsg(null);
    try {
      const r = await fetchWithCsrf(`${API_BASE_URL}/bff/admin/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: announce.title.trim(), body: announce.body.trim() || null }),
      });
      if (!r.ok) throw new Error(((await r.json().catch(() => ({}))).error) || `HTTP ${r.status}`);
      const data = await r.json();
      setAnnounceMsg({ type: 'ok', text: `Sent to ${data.recipients} user(s).` });
      setAnnounce({ title: '', body: '' });
    } catch (err) {
      setAnnounceMsg({ type: 'err', text: err.message || 'Failed to send' });
    } finally {
      setAnnouncing(false);
    }
  };

  const doInspect = async (e) => {
    e.preventDefault();
    const domain = inspectQuery.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '');
    if (!domain) return;
    setInspecting(true); setInspectErr(null); setInspect(null); setRerankMsg(null);
    try {
      const r = await fetch(`${API_BASE_URL}/bff/admin/domains/inspect?domain=${encodeURIComponent(domain)}`, { credentials: 'include' });
      if (r.status === 404) { setInspectErr(`"${domain}" is not in the corpus yet. Use the Domain explorer above: type it, click Inspect, then "Queue a scan".`); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setInspect(await r.json());
    } catch (err) {
      setInspectErr(err.message || 'Inspect failed');
    } finally {
      setInspecting(false);
    }
  };

  const doRerank = async () => {
    if (!inspect) return;
    setRerankMsg(null);
    try {
      const r = await fetchWithCsrf(`${API_BASE_URL}/bff/admin/rerank`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: inspect.domain, reason: rerankReason.trim() || null }),
      });
      if (r.status === 409) { setRerankMsg({ type: 'err', text: 'A re-rank for this domain is already queued.' }); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const row = await r.json();
      setRerankMsg({ type: 'ok', text: `Queued (#${row.id}). Runs when the scanner consumer goes live post-re-tune.` });
      setRerankReason('');
      setInspect((prev) => (prev ? { ...prev, lastRerank: row } : prev));
      setRerankList((prev) => [{ ...row, requested_by_email: meEmail }, ...prev].slice(0, 10));
    } catch (err) {
      setRerankMsg({ type: 'err', text: err.message || 'Failed to queue re-rank' });
    }
  };

  const input = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm';

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const allowedTabs = permissions ? visibleTabs(permissions) : [];
  const tabs = TABS.filter((t) => allowedTabs.includes(t.id));
  const firstTab = tabs[0]?.id || 'general';
  const tab = tabs.some((t) => t.id === tabParam) ? tabParam : firstTab;
  const selectTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === 'general') next.delete('tab');
    else next.set('tab', id);
    setSearchParams(next, { replace: true });
  };
  // A card shows when its tab is selected and the admin holds the card.
  const show = (id, card) => tab === id && (!card || can(id, card));

  return (
    <div className="h-full overflow-y-auto bg-white flex flex-col">
      <div className="flex-1 w-full max-w-4xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold text-black mb-4">Admin</h1>
        <TabBar tabs={tabs} active={tab} onSelect={selectTab} />

        {/* ============================== Overview / analytics ============================== */}
        {!wantsAnalytics ? null : !analytics ? (
          <p className="text-gray-400 text-sm mb-6">Loading analytics…</p>
        ) : (
          <>
            {show('general', 'overview') && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatCard label="MRR" value={money(analytics.revenue.mrrCents)} sub={`${money(analytics.revenue.arrCents)} ARR`} accent="text-green-700" />
              <StatCard label="Active subscriptions" value={num(analytics.revenue.activeByPlan.reduce((s, p) => s + p.n, 0))} sub={`${analytics.revenue.pendingChurn} ending soon`} />
              <StatCard label="Domains ranked" value={num(analytics.scanner.ranked)} sub={`${num(analytics.scanner.pending)} in rescan queue`} />
              <StatCard label="Total users" value={num(analytics.users.total)} sub={`+${analytics.users.last_30d} in 30d`} />
            </div>
            )}

            {show('scanner', 'stats') && (
            <Section title="Scanner">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <StatCard label="Ranked · 24h" value={num(analytics.scanner.ranked24h)} />
                <StatCard label="Ranked · 7d" value={num(analytics.scanner.ranked7d)} />
                {/* The live work queue (never ranked + past the 60-day stale
                    window), matching the scanner's own selection. The old
                    "Backlog" only counted never-ranked domains, which is 0
                    forever once the corpus has a full pass. */}
                <StatCard label="Rescan queue" value={num(analytics.scanner.pending)} sub={`${num(analytics.scanner.backlog)} never ranked`} />
                <StatCard label="Last ranked" value={timeAgo(analytics.scanner.lastRanked)} accent={isRecent(analytics.scanner.lastRanked) ? 'text-green-700' : 'text-amber-600'} sub={isRecent(analytics.scanner.lastRanked) ? 'scanner active' : 'check scanner'} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <StatCard label="Throughput" value={`${num(analytics.scanner.ratePerHour)}/hr`} sub={`${num(analytics.scanner.ranked1h)} last hour`} />
                <StatCard
                  label="Queue ETA"
                  value={analytics.scanner.etaHours == null ? '—' : analytics.scanner.etaHours >= 48 ? `~${Math.round(analytics.scanner.etaHours / 24)}d` : `~${analytics.scanner.etaHours}h`}
                  sub="drain current queue at 24h rate"
                  accent="text-amber-600"
                />
                <StatCard
                  label="Seed / full ver"
                  value={analytics.scanner.scanMeta ? `v${analytics.scanner.scanMeta.seed_version} / v${analytics.scanner.scanMeta.full_scan_version}` : '—'}
                  sub={headBumpNote(analytics.scanner)}
                />
                <StatCard
                  label="Last head scan"
                  value={analytics.scanner.scanMeta?.last_head_at ? timeAgo(analytics.scanner.scanMeta.last_head_at) : '—'}
                  sub="top-25k refresh"
                />
              </div>
              <p className="text-xs font-medium text-gray-500 mb-2">Risk distribution (ranked domains)</p>
              <div className="space-y-2 mb-5">
                {analytics.scanner.riskDistribution.map((r) => (
                  <Bar key={r.risk_level} label={r.risk_level} n={r.n} max={maxOf(analytics.scanner.riskDistribution)} color={riskColor(r.risk_level)} />
                ))}
              </div>
              <p className="text-xs font-medium text-gray-500 mb-2">Recently ranked</p>
              <div className="divide-y divide-gray-100">
                {analytics.scanner.recent.map((d, i) => (
                  <div key={i} className="py-1.5 flex items-center justify-between text-sm gap-3">
                    <span className="text-black truncate">{d.domain}</span>
                    <span className="text-gray-400 text-xs flex-shrink-0">{d.risk_level} · {Number(d.risk_score).toFixed(1)} · {timeAgo(d.ranked_at)}</span>
                  </div>
                ))}
              </div>
            </Section>
            )}

            {show('general', 'revenue') && (
            <Section title="Revenue & subscriptions">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <StatCard label="MRR" value={money(analytics.revenue.mrrCents)} accent="text-green-700" />
                <StatCard label="ARR" value={money(analytics.revenue.arrCents)} accent="text-green-700" />
                <StatCard label="Pending churn" value={num(analytics.revenue.pendingChurn)} sub="cancel at period end" accent={analytics.revenue.pendingChurn > 0 ? 'text-amber-600' : 'text-black'} />
                <StatCard label="Active subs" value={num(analytics.revenue.activeByPlan.reduce((s, p) => s + p.n, 0))} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">Active by plan</p>
                  {analytics.revenue.activeByPlan.length ? (
                    <div className="space-y-2">{analytics.revenue.activeByPlan.map((p) => <Bar key={p.plan_code} label={p.plan_code} n={p.n} max={maxOf(analytics.revenue.activeByPlan)} />)}</div>
                  ) : <p className="text-sm text-gray-400">No active subscriptions.</p>}
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">All subscriptions by status</p>
                  <div className="space-y-2">{analytics.revenue.byStatus.map((s) => <Bar key={s.status} label={s.status} n={s.n} max={maxOf(analytics.revenue.byStatus)} color={statusColor(s.status)} />)}</div>
                </div>
              </div>
            </Section>
            )}

            {show('general', 'dta') && (
            <Section title="Desktop app (DTA)">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <StatCard label="Domain requests" value={num(analytics.dta.requests.total)} sub={`${analytics.dta.requests.last_7d} in 7d`} />
                <StatCard label="Unique domains" value={num(analytics.dta.requests.unique_domains)} />
                <StatCard label="Users on DTA" value={num(analytics.dta.requests.unique_users)} />
                <StatCard label="Devices" value={num(analytics.dta.devices.total)} sub={`${analytics.dta.devices.active_7d} active · 7d`} />
              </div>
              <p className="text-xs font-medium text-gray-500 mb-2">Top requested domains</p>
              {analytics.dta.topDomains.length ? (
                <div className="space-y-2">{analytics.dta.topDomains.map((d) => <Bar key={d.domain} label={d.domain} n={d.n} max={maxOf(analytics.dta.topDomains)} />)}</div>
              ) : <p className="text-sm text-gray-400">No requests yet.</p>}
            </Section>
            )}

            {show('general', 'growth') && (
            <Section title="Growth & engagement">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <StatCard label="Signups · 7d" value={num(analytics.users.last_7d)} />
                <StatCard label="Signups · 30d" value={num(analytics.users.last_30d)} />
                <StatCard label="IC3 alerts posted" value={num(analytics.ic3.total)} sub={analytics.ic3.last_posted ? `last ${timeAgo(analytics.ic3.last_posted)}` : 'none yet'} />
                <StatCard label="Notif. read rate" value={`${analytics.notifications.total ? Math.round((analytics.notifications.read / analytics.notifications.total) * 100) : 0}%`} sub={`${num(analytics.notifications.read)} of ${num(analytics.notifications.total)}`} />
              </div>
              <p className="text-sm text-gray-600">
                Notifications sent: <b>{num(analytics.notifications.total)}</b> — SCI (domain) <b>{analytics.notifications.sci}</b> · IC3 / PSA <b>{analytics.notifications.ic3_psa}</b>
              </p>
            </Section>
            )}
          </>
        )}

        {show('scanner', 'sourceHealth') && (
        <Section title="Data source health (scanner collectors)">
          {!sourceHealth ? (
            <p className="text-gray-400 text-sm">Loading source health…</p>
          ) : sourceHealth.sources.length === 0 ? (
            <p className="text-sm text-green-700">All collectors healthy — no failures logged.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                <StatCard label="Scans" value={num(sourceHealth.totalScans)} sub={`${num(sourceHealth.recentScans)} in 7d`} />
                <StatCard label="Degraded collectors" value={num(sourceHealth.degradedCount)} accent={sourceHealth.degradedCount > 0 ? 'text-amber-600' : 'text-green-700'} />
                <StatCard label="Total failures" value={num(sourceHealth.totalFailures)} />
                <StatCard label="Worst source" value={sourceHealth.sources[0]?.collector || '—'} sub={sourceHealth.sources[0] ? `${(sourceHealth.sources[0].failureRate * 100).toFixed(0)}% of scans` : ''} accent="text-red-600" />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-400 border-b border-gray-100">
                      <th className="py-1 pr-3">Collector</th>
                      <th className="py-1 pr-3">Coverage</th>
                      <th className="py-1 pr-3">Fail rate</th>
                      <th className="py-1 pr-3">7d</th>
                      <th className="py-1 pr-3">Failures</th>
                      <th className="py-1 pr-3">Top cause</th>
                      <th className="py-1">On fail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sourceHealth.sources.map((s) => {
                      const cov = 1 - s.failureRate;
                      const covColor = cov >= 0.95 ? 'text-green-700' : cov >= 0.85 ? 'text-amber-600' : 'text-red-600';
                      const dr = s.recentFailureRate - s.failureRate;
                      return (
                        <tr key={s.collector} className="border-b border-gray-50">
                          <td className="py-1 pr-3 font-medium text-black">{s.collector}</td>
                          <td className={`py-1 pr-3 tabular-nums ${covColor}`}>{(cov * 100).toFixed(1)}%</td>
                          <td className="py-1 pr-3 tabular-nums text-red-600">{(s.failureRate * 100).toFixed(1)}%</td>
                          <td className="py-1 pr-3 tabular-nums text-gray-600">
                            {(s.recentFailureRate * 100).toFixed(0)}%
                            {dr > 0.02 ? <span className="text-red-500" title="worsening vs all-time"> ▲</span> : dr < -0.02 ? <span className="text-green-600" title="improving vs all-time"> ▼</span> : null}
                          </td>
                          <td className="py-1 pr-3 tabular-nums text-gray-600">{num(s.failures)}</td>
                          <td className="py-1 pr-3 text-gray-600 text-xs">{(s.causes || []).slice(0, 2).map((c) => `${c.cause} ${c.pct}%`).join(' · ') || '—'}</td>
                          <td className="py-1 text-gray-400 text-xs tabular-nums">{s.avgSecs == null ? '—' : `${s.avgSecs}s`}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Coverage = share of scans where the collector returned data (1 − fail rate). Cause is classified from <span className="font-mono">error_message</span> (error_type is always the wrapper exception). 7d ▲ = worsening vs all-time. All scanner collectors are listed; those with no recorded failures show 100% coverage.
              </p>
            </>
          )}
        </Section>
        )}

        {show('scanner', 'inspector') && (
        <Section title="Domain explorer">
          <DomainExplorer />
        </Section>
        )}

        {show('scanner', 'inspector') && (
        <Section title="Domain inspector & re-rank">
          <form onSubmit={doInspect} className="flex items-center gap-2 mb-4">
            <input
              className={input}
              placeholder="Inspect a domain (e.g. stanford.edu)"
              value={inspectQuery}
              onChange={(e) => setInspectQuery(e.target.value)}
            />
            <button type="submit" disabled={inspecting || !inspectQuery.trim()} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex-shrink-0">
              {inspecting ? 'Loading…' : 'Inspect'}
            </button>
          </form>

          {inspectErr && <p className="text-sm text-red-600 mb-3">{inspectErr}</p>}

          {inspect && (
            <div className="space-y-4">
              {/* Rating summary */}
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-semibold text-black">{inspect.domain}</span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${riskColor(inspect.mirror?.risk_level || inspect.assessment?.risk_level)}`}>
                  {inspect.mirror?.risk_level || inspect.assessment?.risk_level || 'unrated'}
                </span>
                {inspect.mirror?.risk_score != null && <span className="text-xs text-gray-500">score {Number(inspect.mirror.risk_score).toFixed(1)}</span>}
                {inspect.mirror?.majestic_rank != null && <span className="text-xs text-gray-500">majestic #{num(inspect.mirror.majestic_rank)}</span>}
                {inspect.mirror?.ranked_at && <span className="text-xs text-gray-400">ranked {timeAgo(inspect.mirror.ranked_at)}</span>}
                {inspect.history.length > 0 && <span className="text-xs text-gray-400">{inspect.history.length} scan{inspect.history.length > 1 ? 's' : ''}</span>}
              </div>

              {/* Flags, grouped by taxonomy */}
              {(() => {
                const flags = inspect.assessment?.flags_fired || [];
                const groups = { gold: [], liveness: [], noise: [], contextual: [] };
                flags.forEach((f) => groups[FLAG_BUCKET[f] || 'contextual'].push(f));
                return ['gold', 'liveness', 'contextual', 'noise'].filter((b) => groups[b].length).map((b) => (
                  <div key={b}>
                    <p className="text-xs font-medium text-gray-500 mb-1">{BUCKET_LABEL[b]}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {groups[b].map((f) => (
                        <span key={f} className={`px-2 py-0.5 rounded border text-[11px] font-mono ${BUCKET_STYLE[b]}`}>{f}</span>
                      ))}
                    </div>
                  </div>
                ));
              })()}
              {(inspect.assessment?.legit_signals || []).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Legit signals</p>
                  <div className="flex flex-wrap gap-1.5">
                    {inspect.assessment.legit_signals.map((f) => (
                      <span key={f} className="px-2 py-0.5 rounded border text-[11px] font-mono bg-green-50 text-green-700 border-green-200">{f}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Score composition (score_breakdown ships with the scanner re-tune; null until then) */}
              {inspect.assessment?.score_breakdown && (() => {
                const bd = inspect.assessment.score_breakdown;
                const cats = Object.entries(bd).filter(([k, v]) => k !== '_meta' && v && typeof v === 'object');
                if (!cats.length) return null;
                const maxAbs = Math.max(1, ...cats.map(([, v]) => Math.abs(v.contribution || 0)));
                return (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">
                      Score composition{bd._meta?.model_version ? ` · model ${bd._meta.model_version}` : ''}
                    </p>
                    <div className="space-y-1">
                      {cats.sort((a, b) => Math.abs(b[1].contribution || 0) - Math.abs(a[1].contribution || 0)).map(([cat, v]) => (
                        <div key={cat} className="flex items-center gap-2 text-xs">
                          <span className="w-36 flex-shrink-0 text-gray-600 font-mono truncate" title={cat}>{cat}</span>
                          {v.excluded ? (
                            <span className="text-gray-400 italic">not measured (collector failed — neutral, not penalized)</span>
                          ) : (
                            <>
                              <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${(v.contribution || 0) < 0 ? 'bg-red-500' : 'bg-blue-500'}`}
                                  style={{ width: `${Math.max(2, Math.round((Math.abs(v.contribution || 0) / maxAbs) * 100))}%` }}
                                />
                              </div>
                              <span className="w-28 text-right tabular-nums text-gray-500 flex-shrink-0">
                                {(v.normalized ?? 0).toFixed(2)} × {v.weight} = <b className={(v.contribution || 0) < 0 ? 'text-red-700' : 'text-gray-700'}>{(v.contribution ?? 0).toFixed(1)}</b>
                              </span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">
                      Time-relative categories (age, cert window, CT history) are as-of the last scan/rescore.
                    </p>
                  </div>
                );
              })()}

              {/* Key facts + data gaps */}
              <p className="text-sm text-gray-600">
                Registrar <b>{inspect.assessment?.registrar || 'unknown'}</b> · Country <b>{inspect.assessment?.country_code || '—'}</b> · Blocklist hits <b>{inspect.assessment?.blocklist_hits ?? 0}</b> · Email auth <b>{[inspect.assessment?.has_spf && 'SPF', inspect.assessment?.has_dmarc && 'DMARC', inspect.assessment?.has_dkim && 'DKIM'].filter(Boolean).join('/') || 'none'}</b> · TLS <b>{inspect.assessment?.tls_issuer || '—'}</b>
              </p>
              {inspect.collectorFailures.length > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
                  Data gaps: {inspect.collectorFailures.map((f) => `${f.collector_name} ×${f.n}`).join(', ')} — missing data can inflate the rating (see audit).
                </p>
              )}

              {/* Re-rank */}
              <div className="border-t border-gray-100 pt-3">
                {inspect.lastRerank && ['queued', 'in_progress'].includes(inspect.lastRerank.status) ? (
                  <p className="text-sm text-gray-600">
                    Re-rank <b>{inspect.lastRerank.status}</b> since {timeAgo(inspect.lastRerank.requested_at)} — runs when the scanner consumer goes live.
                  </p>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      className={`${input} max-w-xs`}
                      placeholder="Reason (optional)"
                      value={rerankReason}
                      onChange={(e) => setRerankReason(e.target.value)}
                    />
                    <button onClick={doRerank} className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex-shrink-0">
                      Queue re-rank
                    </button>
                    {inspect.lastRerank?.status === 'done' && (
                      <span className="text-xs text-gray-400">last re-rank done {timeAgo(inspect.lastRerank.completed_at)} → {inspect.lastRerank.result_risk_level || '—'}</span>
                    )}
                  </div>
                )}
                {rerankMsg && <p className={`text-sm mt-2 ${rerankMsg.type === 'ok' ? 'text-green-600' : 'text-red-600'}`}>{rerankMsg.text}</p>}
              </div>
            </div>
          )}

          {rerankList.length > 0 && (
            <div className="mt-5">
              <p className="text-xs font-medium text-gray-500 mb-2">Recent re-rank requests</p>
              <div className="divide-y divide-gray-100">
                {rerankList.map((r) => (
                  <div key={r.id} className="py-1.5 flex items-center justify-between text-sm gap-3">
                    <span className="text-black truncate">{r.domain}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      <span className={`px-1.5 py-0.5 rounded font-medium ${r.status === 'done' ? 'bg-green-100 text-green-700' : r.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{r.status}</span>
                      {' '}{r.result_risk_level ? `→ ${r.result_risk_level} · ` : ''}{timeAgo(r.requested_at)}{r.requested_by_email ? ` · ${r.requested_by_email}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-gray-400 mt-3">
            Read-only inspection of the scanner corpus. Queued re-ranks are consumed by the scanner after the scoring re-tune lands; flag grouping switches to the scanner&apos;s <span className="font-mono">flag_taxonomy.json</span> when it ships.
          </p>
        </Section>
        )}

        {show('scanner', 'scoring') && <ScoringConsole />}

        {show('intake', 'inbox') && <IntakeInbox Section={Section} />}

        {show('alerts') && (can('alerts', 'alerts', 'read') || can('alerts', 'review', 'preview')) && (
          <AlertPublisher
            Section={Section}
            alertsLevel={['full', 'edit', 'read'].find((l) => can('alerts', 'alerts', l)) || 'none'}
            reviewLevel={['full', 'edit', 'preview'].find((l) => can('alerts', 'review', l)) || 'none'}
          />
        )}

        {show('alerts', 'broadcast') && (
        <Section title="Broadcast Announcement">
          <p className="text-xs text-gray-400 mb-3">Sends an in-app notification to every user (e.g. &quot;Update rolling out next week&quot;). Separate from IC3 alerts.</p>
          <form onSubmit={submitAnnounce} className="space-y-3">
            <input className={input} placeholder="Title *" value={announce.title} onChange={(e) => setAnnounce((a) => ({ ...a, title: e.target.value }))} required />
            <textarea className={`${input} min-h-[80px]`} placeholder="Message (optional)" value={announce.body} onChange={(e) => setAnnounce((a) => ({ ...a, body: e.target.value }))} />
            <div className="flex items-center gap-3">
              <button type="submit" disabled={announcing || !announce.title.trim()} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                {announcing ? 'Sending…' : 'Send to all users'}
              </button>
              {announceMsg && <span className={`text-sm ${announceMsg.type === 'ok' ? 'text-green-600' : 'text-red-600'}`}>{announceMsg.text}</span>}
            </div>
          </form>
        </Section>
        )}

        {show('users', 'directory') && <UsersDirectory Section={Section} users={users} loading={usersLoading} />}

        {show('users', 'access') && (
          <AdminAccess
            Section={Section}
            users={users}
            meEmail={meEmail}
            setRole={setRole}
            setPermissions={setPermissions}
            isElevated={isElevated}
            myPermissions={permissions}
          />
        )}

        {show('general', 'health') && (
        <Section title="System Health">
          {!health ? <p className="text-gray-400 text-sm">Loading…</p> : (
            <p className="text-sm text-gray-700">
              <span className={`inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle ${health.ok ? 'bg-green-500' : 'bg-red-500'}`} />
              <b>Database {health.ok ? 'connected' : 'error'}</b>
              {health.db_time ? ` — server time ${new Date(health.db_time).toLocaleString()}` : ''}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-2">A live query against Postgres; a green dot means the backend and database are up. Platform metrics are in the General tab.</p>
        </Section>
        )}
      </div>
      <DashboardFooter />
    </div>
  );
}
